import {Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';

/**
 * Empêche le focus automatique sur un champ au chargement / changement de page
 * (clavier mobile, surlignage visuel indésirable, calendrier PrimeNG ouvert).
 */
@Injectable({providedIn: 'root'})
export class NavigationFocusService {

	private readonly blurDelaysMs = [0, 50, 150, 350, 600];

	constructor(private readonly router: Router) {
	}

	init(): void {
		this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
		).subscribe(() => this.clearPageFocus());
	}

	/** À appeler après un rendu différé (ex. fin de chargement API). */
	clearPageFocus(): void {
		this.runBlurPasses();
	}

	private runBlurPasses(): void {
		for (const delayMs of this.blurDelaysMs) {
			if (delayMs === 0) {
				this.blurAndCloseOverlays();
				requestAnimationFrame(() => {
					requestAnimationFrame(() => this.blurAndCloseOverlays());
				});
				continue;
			}
			setTimeout(() => this.blurAndCloseOverlays(), delayMs);
		}
	}

	private blurAndCloseOverlays(): void {
		if (this.shouldPreserveFocus()) {
			return;
		}

		this.stripDatepickerAutofocusAttributes();
		this.closeDatepickerOverlays();

		const active = document.activeElement as HTMLElement | null;
		if (active?.blur && active !== document.body && active !== document.documentElement) {
			active.blur();
		}
	}

	/** Ne pas voler le focus pendant une saisie dans une modale ou un panneau overlay. */
	private shouldPreserveFocus(): boolean {
		const active = document.activeElement as HTMLElement | null;
		if (active?.closest('.p-dialog, .p-drawer, .p-overlaypanel, .p-popover, .p-datepicker, .p-datepicker-panel')) {
			return true;
		}
		return !!document.querySelector('.p-dialog-mask, .p-drawer-mask, .p-datepicker-panel');
	}

	/**
	 * PrimeNG DatePicker lie [pAutoFocus]="autofocus" sans valeur par défaut :
	 * la directive pose alors l'attribut HTML autofocus sur l'input.
	 */
	private stripDatepickerAutofocusAttributes(): void {
		document.querySelectorAll<HTMLElement>(
			'.p-datepicker [autofocus], .p-datepicker-panel [autofocus]',
		).forEach(element => element.removeAttribute('autofocus'));
	}

	private closeDatepickerOverlays(): void {
		const panel = document.querySelector('.p-datepicker-panel');
		if (!panel || this.shouldPreserveFocus()) {
			return;
		}
		const active = document.activeElement as HTMLElement | null;
		if (active?.closest('.p-datepicker')) {
			return;
		}
		document.dispatchEvent(new KeyboardEvent('keydown', {key: 'Escape', bubbles: true}));
	}

}
